import React, { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import {
  Box, Button, Typography, Stepper, Step, StepLabel, StepConnector, Alert,
  CircularProgress, Fade, LinearProgress, stepConnectorClasses, styled,
} from '@mui/material';
import {
  ArrowBack, ArrowForward, CheckCircle, RocketLaunch,
  LocalHospital as HospitalIcon,
  Badge as BadgeIcon,
  AdminPanelSettings as AdminIcon,
  Tune as SettingsIcon,
  FactCheck as ReviewIcon,
  Check as CheckMark,
} from '@mui/icons-material';

import { BRAND, PRODUCT, FONT_STACK, brandButtonSx, brandGhostButtonSx } from '../../brand';
import { setup_complete } from '../BaseURL';
import BrandPanel, { MobileBrandBar } from './BrandPanel';
import FacilityStep from './steps/FacilityStep';
import IdentityStep from './steps/IdentityStep';
import AdminStep from './steps/AdminStep';
import InstallStep from './steps/InstallStep';
import ReviewStep from './steps/ReviewStep';

// ============================================================================
//  SetupWizard — the first screen a buyer ever sees
// ============================================================================
//
//  Rendered by SystemStart when GET /setup/status reports setupRequired. Four
//  steps, validated locally on every "Continue" and re-validated server-side
//  in one transaction on "Complete Setup".
//
//  State lives here rather than in the steps because the Review step needs to
//  read everything at once, and because a mis-typed field on step 1 must
//  survive a trip back from step 4.
//
//  Props
//    activationRequired  does this install carry an activation key?
//    onComplete(result)  called after a successful POST; SystemStart uses it
//                        to swap the wizard out for the login screen

const STEPS = [
  { label: 'Facility',      icon: HospitalIcon },
  { label: 'Identity',      icon: BadgeIcon },
  { label: 'Administrator', icon: AdminIcon },
  // Placed after the people and before the review: it is a decision about the
  // INSTALLATION rather than about the facility or the operator, and the review
  // step needs to be able to show what was chosen.
  { label: 'Install type',  icon: SettingsIcon },
  { label: 'Review',        icon: ReviewIcon },
];

// ── Stepper chrome ──────────────────────────────────────────────────────────
const BrandConnector = styled(StepConnector)(() => ({
  [`&.${stepConnectorClasses.alternativeLabel}`]: { top: 17 },
  [`&.${stepConnectorClasses.active} .${stepConnectorClasses.line}`]: { borderColor: BRAND.green },
  [`&.${stepConnectorClasses.completed} .${stepConnectorClasses.line}`]: { borderColor: BRAND.green },
  [`& .${stepConnectorClasses.line}`]: { borderColor: BRAND.line, borderTopWidth: 2, borderRadius: 1 },
}));

function BrandStepIcon({ active, completed, icon: Icon }) {
  const bg = completed ? BRAND.green : active ? BRAND.navy : '#fff';
  const fg = completed || active ? '#fff' : BRAND.inkFaint;
  return (
    <Box
      sx={{
        width: 36, height: 36, borderRadius: '50%',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        background: bg,
        border: completed || active ? 'none' : `2px solid ${BRAND.line}`,
        boxShadow: active ? `0 0 0 4px ${BRAND.navy}1A` : 'none',
        transition: 'all .25s ease',
      }}
    >
      {completed ? <CheckMark sx={{ fontSize: 19, color: fg }} /> : <Icon sx={{ fontSize: 18, color: fg }} />}
    </Box>
  );
}

// ── Field defaults ──────────────────────────────────────────────────────────
const EMPTY_FACILITY = {
  name: '', type: '', ownership: '', region: '', district: '', location: '',
  digitalAddress: '', poBox: '', bedCapacity: '',
  email: '', phone: '', website: '', motto: '', licenseNo: '', nhisCode: '',
  logo: '', country: 'Ghana',
};

const EMPTY_ADMIN = {
  fullName: '', staffId: '', gender: '', phone: '', email: '',
  username: '', password: '', confirmPassword: '',
};

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
const PHONE_RE = /^[0-9+\-\s]{9,20}$/;

// The server disambiguates the two email/phone pairs (facility vs admin)
// because it validates both in one payload; the wizard shows only one step at
// a time and keys those fields plainly. Translate on the way back so a
// server-side rejection lands on the field that actually renders it.
const SERVER_ERROR_KEYS = {
  facilityEmail: 'email',
  facilityPhone: 'phone',
  adminEmail:    'email',
};

function mapServerErrors(errors) {
  if (!errors) return {};
  return Object.entries(errors).reduce((acc, [key, message]) => {
    acc[SERVER_ERROR_KEYS[key] || key] = message;
    return acc;
  }, {});
}

// Which step owns each field — so a rejected submit reopens the step the
// operator has to fix rather than leaving them on the review screen.
const FIELD_STEP = {
  name: 0, type: 0, region: 0, district: 0, location: 0, bedCapacity: 0,
  facilityEmail: 1, facilityPhone: 1, logo: 1,
  fullName: 2, staffId: 2, adminEmail: 2, username: 2, password: 2, confirmPassword: 2,
  // 4, not 3 — the activation key lives on the Review step, which moved back one
  // when the Install type step was inserted before it. Left at 3 this would send
  // a bad activation key to the Install step, which has no field to highlight.
  activationKey: 4,
};

// 'YYYY-MM-DD' from the licence DB → '4 August 2027'. Parsed by parts rather
// than by `new Date(string)` so the date shown is the date stored, not the
// stored date shifted by the browser's UTC offset.
function formatLicenceDate(value) {
  if (!value) return 'the date on your licence';
  const [y, m, d] = String(value).slice(0, 10).split('-').map(Number);
  if (!y || !m || !d) return String(value);
  return new Date(y, m - 1, d).toLocaleDateString('en-GB', {
    day: 'numeric', month: 'long', year: 'numeric',
  });
}

function firstErroredStep(errors) {
  const steps = Object.keys(errors || {})
    .map((k) => FIELD_STEP[k])
    .filter((s) => s !== undefined);
  return steps.length ? Math.min(...steps) : null;
}

function SetupWizard({ activationRequired = false, onComplete }) {
  const [step, setStep]       = useState(0);
  const [facility, setFacility] = useState(EMPTY_FACILITY);
  const [admin, setAdmin]     = useState(EMPTY_ADMIN);
  const [activationKey, setActivationKey] = useState('');
  const [errors, setErrors]   = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [done, setDone]       = useState(null);
  const [usernameStatus, setUsernameStatus] = useState(null);
  // What kind of installation this is, and which departments it gets.
  // `departments: null` means no restriction — every department.
  const [install, setInstall] = useState({ mode: 'facility', departments: null });
  const setInstallField = (k, v) => setInstall((p) => ({ ...p, [k]: v }));

  const setFacilityField = (field, value) => {
    setFacility((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => ({ ...prev, [field]: undefined }));
  };
  const setAdminField = (field, value) => {
    setAdmin((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => ({ ...prev, [field]: undefined }));
  };

  // ── Per-step validation ───────────────────────────────────────────────────
  // Each step validates only its own fields so moving backwards never
  // surfaces errors for fields the operator has not reached yet.
  const validateStep = (index) => {
    const next = {};

    if (index === 0) {
      if (!facility.name.trim() || facility.name.trim().length < 3) {
        next.name = 'Enter the facility name (at least 3 characters)';
      }
      if (!facility.type) next.type = 'Select the facility type';
      if (!facility.region) next.region = 'Select the region';
      if (!facility.location.trim()) next.location = 'Enter the town or location';
      if (facility.bedCapacity && Number(facility.bedCapacity) < 0) {
        next.bedCapacity = 'Bed capacity cannot be negative';
      }
    }

    if (index === 1) {
      if (!EMAIL_RE.test(facility.email.trim())) next.email = 'Enter a valid email address';
      if (!PHONE_RE.test(facility.phone.trim()))  next.phone = 'Enter a valid phone number';
      // Required: the logo is printed on ID cards, receipts and lab reports.
      // Nothing downstream prompts for it, so it has to be caught here.
      if (!facility.logo) next.logo = 'Upload your facility logo to continue';
    }

    if (index === 2) {
      if (!admin.fullName.trim()) next.fullName = 'Enter the administrator’s full name';
      if (!admin.staffId.trim())  next.staffId  = 'Enter a staff ID';
      if (!EMAIL_RE.test(admin.email.trim())) next.email = 'Enter a valid email address';
      if (admin.phone.trim() && !PHONE_RE.test(admin.phone.trim())) {
        next.phone = 'Enter a valid phone number';
      }

      const username = admin.username.trim();
      if (!username) {
        next.username = 'Choose a username';
      } else if (!/^[A-Za-z0-9._-]{4,32}$/.test(username)) {
        next.username = 'Username must be 4–32 characters — letters, numbers, dot, underscore or hyphen';
      } else if (usernameStatus === 'taken') {
        next.username = 'That username is already taken';
      }

      // No complexity rules here, on purpose. This is a handover password:
      // the account is created with first_login = 1, so the administrator is
      // forced to replace it before reaching anything, and the full policy
      // (length, upper, lower, number, symbol) is enforced at that point.
      // Gating it here would only make installation fiddly for a credential
      // that lives for minutes. Presence and confirmation are all that matter.
      const pw = admin.password || '';
      if (!pw) next.password = 'Enter a password';

      if (admin.confirmPassword !== pw) next.confirmPassword = 'Passwords do not match';
    }

    // Step 3 (Install type): a mode is always selected, and "all departments"
    // is valid — but ZERO departments is not, and it became reachable when the
    // Clear all button was added to that step.
    //
    // This guard is not cosmetic. `departments: []` and "no restriction" are
    // indistinguishable on the wire: SetupWizard posts `[]` for both, and
    // installProfile.departments() reads an absent or empty list as null = all.
    // So an install submitted with nothing ticked would come back with EVERY
    // department enabled — the exact opposite of what the operator chose, with
    // nothing on screen to say so. Blocking the empty state means the ambiguity
    // can never be submitted, so it can never be misread.
    if (index === 3 && Array.isArray(install.departments) && install.departments.length === 0) {
      next.departments = 'Choose at least one department for this installation';
    }

    // Step 4 is Review, where the activation key is entered.
    if (index === 4 && activationRequired && !activationKey.trim()) {
      next.activationKey = 'Enter your activation key';
    }

    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleNext = () => {
    if (!validateStep(step)) return;
    setSubmitError('');
    setStep((s) => Math.min(s + 1, STEPS.length - 1));
  };

  const handleBack = () => {
    setErrors({});
    setSubmitError('');
    setStep((s) => Math.max(s - 1, 0));
  };

  // ── Commit ────────────────────────────────────────────────────────────────
  // Re-runs every step's validation before posting: the operator can reach
  // step 4 and then walk back and blank a required field.
  const handleSubmit = async () => {
    setSubmitError('');

    for (let i = 0; i < STEPS.length; i += 1) {
      if (!validateStep(i)) {
        setStep(i);
        return;
      }
    }

    setSubmitting(true);
    try {
      const { data } = await axios.post(setup_complete, {
        facility: {
          ...facility,
          bedCapacity: facility.bedCapacity === '' ? null : Number(facility.bedCapacity),
        },
        admin: {
          fullName: admin.fullName.trim(),
          staffId:  admin.staffId.trim(),
          gender:   admin.gender,
          phone:    admin.phone.trim(),
          email:    admin.email.trim(),
          username: admin.username.trim(),
          password: admin.password,
        },
        // What kind of installation this is. The backend defaults to
        // facility / all departments if this is absent, so an older frontend
        // posting to a newer server still completes setup sensibly.
        install: {
          mode: install.mode,
          // null means "all" — send an empty array rather than null so the
          // backend's Array.isArray check treats it as "not specified" and
          // applies its own default, which is the same thing.
          departments: Array.isArray(install.departments) ? install.departments : [],
        },
        activationKey: activationKey.trim(),
      });

      if (data.success) {
        setDone(data);
      } else {
        setErrors(mapServerErrors(data.errors));
        setSubmitError(data.message || 'Setup could not be completed.');
        const target = firstErroredStep(data.errors);
        if (target !== null) setStep(target);
      }
    } catch (err) {
      const res = err.response?.data;
      // The server owns the final word on field validity — surface its
      // per-field errors on the fields that render them, and reopen the step
      // that needs fixing rather than stranding the operator on Review.
      if (res?.errors) {
        setErrors(mapServerErrors(res.errors));
        const target = firstErroredStep(res.errors);
        if (target !== null && target !== 3) setStep(target);
      }
      // 413 is almost always the logo. Say that, rather than leaving the
      // operator staring at a generic network error with no idea which of
      // fifteen fields is at fault.
      if (err.response?.status === 413) {
        setSubmitError(
          'The details could not be sent because the facility logo is too large. ' +
          'Go back to the Identity step and choose a smaller image.'
        );
        setStep(1);
        return;
      }

      setSubmitError(
        res?.message ||
        'Could not reach the server. Check the connection and try again.'
      );
      // A 409 means someone completed setup from another machine. Nothing
      // useful remains on this screen, so hand back to SystemStart.
      if (err.response?.status === 409 && res?.alreadyComplete) {
        setTimeout(() => onComplete?.({ alreadyComplete: true }), 2200);
      }
    } finally {
      setSubmitting(false);
    }
  };

  const progress = useMemo(() => ((step + 1) / STEPS.length) * 100, [step]);

  // Each step starts at the top. Without this, advancing from the bottom of a
  // long step drops you into the middle of the next one — which on a phone
  // reads as the form having skipped fields.
  const bodyRef = useRef(null);
  useEffect(() => {
    bodyRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
  }, [step]);

  // ── Success screen ────────────────────────────────────────────────────────
  if (done) {
    return (
      // placeItems + overflowY lets the card centre on a tall screen but still
      // scroll on a short one, instead of clipping its own button.
      <Box
        sx={{
          minHeight: '100vh',
          '@supports (min-height: 100dvh)': { minHeight: '100dvh' },
          display: 'grid', placeItems: 'center', overflowY: 'auto',
          background: BRAND.canvas, fontFamily: FONT_STACK,
          p: { xs: 2, sm: 3 },
        }}
      >
        <Fade in timeout={500}>
          <Box sx={{ maxWidth: 520, width: '100%', textAlign: 'center', background: '#fff', borderRadius: '20px', p: { xs: 3, sm: 5, md: 6 }, border: `1px solid ${BRAND.line}`, boxShadow: '0 20px 60px rgba(15,31,61,.10)' }}>
            <Box sx={{ width: { xs: 64, sm: 76 }, height: { xs: 64, sm: 76 }, borderRadius: '50%', mx: 'auto', mb: 3, display: 'grid', placeItems: 'center', background: BRAND.greenSoft }}>
              <CheckCircle sx={{ fontSize: { xs: 36, sm: 44 }, color: BRAND.green }} />
            </Box>

            <Typography sx={{ fontSize: { xs: 21, sm: 25 }, fontWeight: 800, color: BRAND.ink, mb: 1.25 }}>
              {PRODUCT.name} is ready
            </Typography>
            <Typography sx={{ fontSize: 14.5, color: BRAND.inkMuted, lineHeight: 1.75, mb: 3.5 }}>
              <strong>{done.facility?.name}</strong> has been registered and the super-administrator
              account is active.
            </Typography>

            <Box sx={{ textAlign: 'left', borderRadius: '12px', border: `1px solid ${BRAND.line}`, background: BRAND.canvas, px: 2.5, py: 2, mb: 3.5 }}>
              <Typography sx={{ fontSize: 11, fontWeight: 800, letterSpacing: '1px', textTransform: 'uppercase', color: BRAND.inkFaint, mb: 1 }}>
                Your sign-in username
              </Typography>
              <Typography
                sx={{
                  fontSize: { xs: 17, sm: 19 }, fontWeight: 800, color: BRAND.navy,
                  fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                  wordBreak: 'break-all',
                }}
              >
                {done.admin?.username}
              </Typography>
              <Typography sx={{ fontSize: 12.5, color: BRAND.inkMuted, mt: 1.25, lineHeight: 1.65 }}>
                Write this down — it is permanent. Sign in with the password you just
                set; the system will immediately ask you to replace it with one only
                you know.
              </Typography>
            </Box>

            {/* Licence. The server issues this itself now, so the usual case
                is a quiet one-liner. It only earns real estate when it
                FAILED — because then nobody can sign in, and saying so here
                is far kinder than a "No license found" wall at the login
                screen with no explanation of what went wrong. */}
            {done.license && (
              <Box
                sx={{
                  textAlign: 'left', borderRadius: '12px', px: 2.5, py: 1.75, mb: 3.5,
                  border: `1px solid ${done.license.provisioned ? BRAND.line : '#F4C46B'}`,
                  background: done.license.provisioned ? BRAND.canvas : '#FFF8EC',
                }}
              >
                <Typography sx={{ fontSize: 11, fontWeight: 800, letterSpacing: '1px', textTransform: 'uppercase', color: done.license.provisioned ? BRAND.inkFaint : '#9A6206', mb: 0.75 }}>
                  {done.license.provisioned ? 'System licence' : 'Licence needs attention'}
                </Typography>
                <Typography sx={{ fontSize: 13, color: done.license.provisioned ? BRAND.inkMuted : '#7A4E05', lineHeight: 1.65 }}>
                  {done.license.provisioned
                    ? `Active until ${formatLicenceDate(done.license.expiresAt)}. Renew with TotalCare before that date to avoid interruption.`
                    : done.license.message}
                </Typography>
              </Box>
            )}

            <Button
              fullWidth variant="contained" size="large"
              endIcon={<ArrowForward />}
              onClick={() => onComplete?.(done)}
              sx={{ ...brandButtonSx, py: 1.4, fontSize: 15 }}
            >
              Continue to sign in
            </Button>
          </Box>
        </Fade>
      </Box>
    );
  }

  // ── Wizard ────────────────────────────────────────────────────────────────
  const isLast = step === STEPS.length - 1;

  return (
    // Locked to the viewport height, not `minHeight` — that is what pins the
    // footer to the bottom of the screen instead of letting the page grow and
    // leaving dead space beneath it. `100dvh` where supported so mobile
    // browsers measure against the *visible* area, excluding the collapsing
    // address bar; 100vh is the fallback.
    <Box
      sx={{
        height: '100vh',
        '@supports (height: 100dvh)': { height: '100dvh' },
        overflow: 'hidden',
        background: BRAND.canvas,
        fontFamily: FONT_STACK,
        display: 'flex',
      }}
    >
      {/* Brand panel — a side column only where there is genuinely room for
          it. Below lg it becomes MobileBrandBar, inside the form column. */}
      <Box
        sx={{
          width: { lg: 380, xl: 460 },
          flexShrink: 0,
          display: { xs: 'none', lg: 'block' },
          height: '100%',
          overflowY: 'auto',
        }}
      >
        <BrandPanel />
      </Box>

      {/* Form column. minHeight/minWidth 0 let the scroll area actually
          shrink — without them a flex child refuses to go below its content
          height and the whole page scrolls instead of just the body. */}
      <Box
        sx={{
          flex: 1, minWidth: 0, minHeight: 0, height: '100%',
          display: 'flex', flexDirection: 'column',
        }}
      >
        <MobileBrandBar />

        {/* Header: progress + stepper.
            Capped and centred to the same 1080 as the form body so the
            stepper sits directly above the fields it labels. */}
        <Box
          sx={{
            flexShrink: 0,
            px: { xs: 2, sm: 3, md: 5 },
            pt: { xs: 2, md: 3 },
            pb: { xs: 1.5, md: 2 },
            background: '#fff',
            borderBottom: `1px solid ${BRAND.line}`,
          }}
        >
         <Box sx={{ width: '100%', maxWidth: 1080, mx: 'auto' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, mb: { xs: 1.5, md: 2 } }}>
            <Box sx={{ minWidth: 0 }}>
              <Typography
                sx={{
                  fontSize: { xs: 9.5, md: 11 }, fontWeight: 800, letterSpacing: '1.5px',
                  textTransform: 'uppercase', color: BRAND.green, mb: .25,
                }}
              >
                First-time setup
              </Typography>
              <Typography
                sx={{ fontSize: { xs: 17, md: 21 }, fontWeight: 800, color: BRAND.ink, letterSpacing: '-.4px' }}
                noWrap
              >
                Set up your system
              </Typography>
            </Box>

            {/* On a phone the icon rail is dropped, so this label carries the
                whole sense of position — it names the step as well as the
                number. */}
            <Box sx={{ textAlign: 'right', flexShrink: 0 }}>
              <Typography sx={{ fontSize: { xs: 12, md: 13 }, fontWeight: 700, color: BRAND.inkMuted }}>
                Step {step + 1} of {STEPS.length}
              </Typography>
              <Typography
                sx={{ display: { xs: 'block', sm: 'none' }, fontSize: 11.5, fontWeight: 700, color: BRAND.navy }}
              >
                {STEPS[step].label}
              </Typography>
            </Box>
          </Box>

          <LinearProgress
            variant="determinate" value={progress}
            sx={{
              height: 4, borderRadius: 2,
              mb: { xs: 0, sm: 2 },
              backgroundColor: BRAND.line,
              '& .MuiLinearProgress-bar': { backgroundColor: BRAND.green, borderRadius: 2 },
            }}
          />

          {/* Four labelled icons need ~110px each. Below sm they would either
              collide or truncate to initials, so the progress bar plus the
              step name above does the job instead. */}
          <Stepper
            activeStep={step} alternativeLabel connector={<BrandConnector />}
            sx={{ display: { xs: 'none', sm: 'flex' } }}
          >
            {STEPS.map((s, i) => (
              <Step key={s.label}>
                <StepLabel
                  StepIconComponent={(props) => <BrandStepIcon {...props} icon={s.icon} />}
                  sx={{
                    '& .MuiStepLabel-label': {
                      fontSize: { sm: 11.5, md: 12.5 }, mt: '5px !important',
                      color: i === step ? BRAND.navy : BRAND.inkMuted,
                      fontWeight: i === step ? 700 : 500,
                    },
                    '& .MuiStepLabel-label.Mui-completed': { color: BRAND.green, fontWeight: 600 },
                  }}
                >
                  {s.label}
                </StepLabel>
              </Step>
            ))}
          </Stepper>
         </Box>
        </Box>

        {/* Scrolling step body — the ONLY scroll region on the screen.
            `minHeight: 0` is what allows it to be shorter than its content;
            without it the flex parent grows and the page scrolls instead. */}
        <Box
          ref={bodyRef}
          sx={{
            flex: 1, minHeight: 0, overflowY: 'auto', overflowX: 'hidden',
            WebkitOverflowScrolling: 'touch',
            px: { xs: 2, sm: 3, md: 5 },
            py: { xs: 2.5, md: 3.5 },
          }}
        >
            <Fade in key={step} timeout={320}>
              {/* Wide enough for a real three-across row without the fields
                  becoming letterboxes on a 1080p screen. Centred rather than
                  left-pinned so the column does not drift away from the
                  stepper above it on very wide monitors. */}
              <Box sx={{ width: '100%', maxWidth: 1080, mx: 'auto' }}>
                {/* A server rejection can bounce the operator back to an
                    earlier step; the banner follows them there. Step 3 renders
                    its own copy inside ReviewStep, so skip it here. */}
                {submitError && step !== 3 && (
                  <Alert severity="error" sx={{ mb: 3, borderRadius: '12px' }}>{submitError}</Alert>
                )}
                {step === 0 && (
                  <FacilityStep data={facility} errors={errors} onChange={setFacilityField} />
                )}
                {step === 1 && (
                  <IdentityStep data={facility} errors={errors} onChange={setFacilityField} />
                )}
                {step === 2 && (
                  <AdminStep
                    data={admin} errors={errors} onChange={setAdminField}
                    onUsernameStatus={setUsernameStatus}
                  />
                )}
                {step === 3 && (
                  <InstallStep data={install} onChange={setInstallField} error={errors.departments} />
                )}
                {step === 4 && (
                  <ReviewStep
                    facility={facility}
                    admin={admin}
                    install={install}
                    activationKey={activationKey}
                    activationRequired={activationRequired}
                    errors={errors}
                    onChange={(v) => { setActivationKey(v); setErrors((p) => ({ ...p, activationKey: undefined })); }}
                    submitError={submitError}
                  />
                )}
              </Box>
            </Fade>
          </Box>

        {/* Footer actions — pinned to the bottom of the viewport by the
            fixed-height flex column above, so it never floats mid-page with
            empty canvas beneath it. Same 1080 cap so Back and Continue line
            up with the left and right edges of the form. */}
        <Box
          sx={{
            flexShrink: 0,
            px: { xs: 2, sm: 3, md: 5 },
            py: { xs: 1.5, md: 2 },
            // Clears the iOS home indicator / Android gesture bar.
            pb: { xs: 'calc(12px + env(safe-area-inset-bottom))', md: 2 },
            background: '#fff',
            borderTop: `1px solid ${BRAND.line}`,
            boxShadow: '0 -2px 12px rgba(15,31,61,.05)',
          }}
        >
         <Box
          sx={{
            width: '100%', maxWidth: 1080, mx: 'auto',
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            gap: { xs: 1.5, md: 2 },
          }}
         >
          <Button
            variant="outlined" startIcon={<ArrowBack />}
            onClick={handleBack} disabled={step === 0 || submitting}
            sx={{
              ...brandGhostButtonSx,
              px: { xs: 2, md: 3 }, py: { xs: .9, md: 1.05 },
              fontSize: { xs: 13.5, md: 14 },
              flexShrink: 0,
            }}
          >
            Back
          </Button>

          {/* Attribution is the first thing to go when the row gets tight —
              the two buttons must never be squeezed. */}
          <Typography
            sx={{ fontSize: 12, color: BRAND.inkFaint, display: { xs: 'none', md: 'block' } }}
          >
            {PRODUCT.vendor} · {PRODUCT.name}
          </Typography>

          {isLast ? (
            <Button
              variant="contained" disabled={submitting}
              startIcon={submitting ? <CircularProgress size={17} color="inherit" /> : <RocketLaunch />}
              onClick={handleSubmit}
              sx={{
                ...brandButtonSx,
                px: { xs: 2.5, md: 4 }, py: { xs: .9, md: 1.05 },
                fontSize: { xs: 13.5, md: 14 },
                flexShrink: 0,
              }}
            >
              {submitting ? 'Setting up…' : 'Complete Setup'}
            </Button>
          ) : (
            <Button
              variant="contained" endIcon={<ArrowForward />}
              onClick={handleNext}
              sx={{
                ...brandButtonSx,
                px: { xs: 3, md: 4 }, py: { xs: .9, md: 1.05 },
                fontSize: { xs: 13.5, md: 14 },
                flexShrink: 0,
              }}
            >
              Continue
            </Button>
          )}
         </Box>
        </Box>
      </Box>
    </Box>
  );
}

export default SetupWizard;
